import { AlertType, EmailTemplate } from '@types';
import { AlertModal, Card, PageHeader } from '_atoms';
import { AdminService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdClose,
  MdEdit,
  MdEmail,
  MdExpandLess,
  MdExpandMore,
  MdSave,
} from 'react-icons/md';

interface VariableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (variables: Record<string, string>) => void;
  variables: string;
  templateSubject: string;
}

const VariableModal: React.FC<VariableModalProps> = ({
  isOpen,
  onClose,
  onSend,
  variables,
  templateSubject,
}) => {
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    if (isOpen && variables) {
      const variableList = variables
        .split(',')
        .map(v => v.trim())
        .filter(v => v);
      const initialValues: Record<string, string> = {};
      variableList.forEach(variable => {
        initialValues[variable] = '';
      });
      setVariableValues(initialValues);
    }
  }, [isOpen, variables]);

  const handleSend = () => {
    onSend(variableValues);
    onClose();
  };

  if (!isOpen) return null;

  const variableList = variables
    ? variables
        .split(',')
        .map(v => v.trim())
        .filter(v => v)
    : [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <MdEmail className="text-blue-600" size={24} />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Send Test Email
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <MdClose size={20} />
                  </button>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    Template: <strong>{templateSubject}</strong>
                  </p>
                  {variableList.length > 0 ? (
                    <div className="mt-4 space-y-3">
                      <p className="text-sm font-medium text-gray-700">
                        Enter values for the following variables:
                      </p>
                      {variableList.map(variable => (
                        <div key={variable}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {variable}
                          </label>
                          <input
                            type="text"
                            value={variableValues[variable] || ''}
                            onChange={e =>
                              setVariableValues(prev => ({
                                ...prev,
                                [variable]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={`Enter value for ${variable}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2">
                      No variables defined for this template.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSend}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200"
            >
              Send Test Email
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailsPage: React.FC = () => {
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(
    new Set(),
  );
  const [editingTemplates, setEditingTemplates] = useState<Set<string>>(
    new Set(),
  );
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [variableModal, setVariableModal] = useState<{
    isOpen: boolean;
    templateId: string;
    variables: string;
    templateSubject: string;
  }>({
    isOpen: false,
    templateId: '',
    variables: '',
    templateSubject: '',
  });
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleTemplateChange = (
    templateId: string,
    field: keyof EmailTemplate,
    value: string,
  ) => {
    setEmailTemplates(prev =>
      prev.map(template =>
        template.id === templateId ? { ...template, [field]: value } : template,
      ),
    );
  };

  const handleEditToggle = (templateId: string) => {
    setEditingTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
        // Always expand when entering edit mode
        setExpandedTemplates(prevExpanded => {
          const newExpandedSet = new Set(prevExpanded);
          newExpandedSet.add(templateId);
          return newExpandedSet;
        });
      }
      return newSet;
    });
  };

  const handleExpandToggle = (templateId: string) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleSaveTemplate = useCallback(
    async (template: EmailTemplate) => {
      setIsLoadingSave(true);

      try {
        await AdminService.updateEmailTemplate(template.id, template);
        showAlert('success', 'Success', 'Email template updated successfully!');
        setEditingTemplates(prev => {
          const newSet = new Set(prev);
          newSet.delete(template.id);
          return newSet;
        });
      } catch (error) {
        console.error('Error updating email template:', error);
        showAlert(
          'error',
          'Error',
          'Error updating email template. Please try again.',
        );
      } finally {
        setIsLoadingSave(false);
      }
    },
    [showAlert],
  );

  const handleSendTestEmail = useCallback(
    async (templateId: string, variables?: Record<string, string>) => {
      try {
        await AdminService.sendTestEmail(templateId, variables);
        showAlert('success', 'Success', 'Test email sent successfully!');
      } catch (error) {
        console.error('Error sending test email:', error);
        showAlert(
          'error',
          'Error',
          'Error sending test email. Please try again.',
        );
      }
    },
    [showAlert],
  );

  const openVariableModal = (template: EmailTemplate) => {
    setVariableModal({
      isOpen: true,
      templateId: template.id,
      variables: template.variables || '',
      templateSubject: template.subject,
    });
  };

  const closeVariableModal = () => {
    setVariableModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleSendWithVariables = (variables: Record<string, string>) => {
    handleSendTestEmail(variableModal.templateId, variables);
  };

  // Load email templates on page load
  useEffect(() => {
    const loadEmailTemplates = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await AdminService.getEmailTemplates()) as unknown as EmailTemplate[];

        setEmailTemplates(responseData);
      } catch (error) {
        console.error('Error loading email templates:', error);
        showAlert('error', 'Error', 'Failed to load email templates.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadEmailTemplates();
  }, [showAlert]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdEmail}
        title="Email Templates Management"
        description="Configure email templates for various notifications and communications."
      />

      {/* Main Content */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading email templates...</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {emailTemplates.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <p className="text-gray-600">No email templates found.</p>
              </div>
            </Card>
          ) : (
            emailTemplates.map(template => {
              const isExpanded = expandedTemplates.has(template.id);
              const isEditing = editingTemplates.has(template.id);

              return (
                <Card key={template.id} className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleExpandToggle(template.id)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        {isExpanded ? (
                          <MdExpandLess className="w-5 h-5 text-gray-600" />
                        ) : (
                          <MdExpandMore className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {template.subject}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {template.description}
                        </p>
                        <p className="text-xs text-gray-500">
                          Category: {template.category}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openVariableModal(template)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        <MdEmail className="mr-1" />
                        Send Test
                      </button>
                      <button
                        onClick={() => handleEditToggle(template.id)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        {isEditing ? (
                          <MdClose className="mr-1" />
                        ) : (
                          <MdEdit className="mr-1" />
                        )}
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 space-y-4">
                      {isEditing ? (
                        // Edit view
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Subject
                            </label>
                            <input
                              type="text"
                              value={template.subject}
                              onChange={e =>
                                handleTemplateChange(
                                  template.id,
                                  'subject',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <input
                              type="text"
                              value={template.description}
                              onChange={e =>
                                handleTemplateChange(
                                  template.id,
                                  'description',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Variables (comma-separated)
                            </label>
                            <input
                              type="text"
                              value={template.variables}
                              onChange={e =>
                                handleTemplateChange(
                                  template.id,
                                  'variables',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="e.g., customerName, orderNumber, date"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Use these variables in the email body with{' '}
                              {'{{variableName}}'} syntax
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Test Recipients (comma-separated emails)
                            </label>
                            <input
                              type="text"
                              value={template.testRecipients}
                              onChange={e =>
                                handleTemplateChange(
                                  template.id,
                                  'testRecipients',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="test@example.com, another@example.com"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Body (HTML supported)
                            </label>
                            <textarea
                              value={template.body}
                              onChange={e =>
                                handleTemplateChange(
                                  template.id,
                                  'body',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[200px] resize-y"
                              placeholder="Enter email body here... HTML tags are supported for formatting."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              You can use HTML tags for formatting and{' '}
                              {'{{variableName}}'} for dynamic content
                            </p>
                          </div>

                          {/* Preview Section */}
                          {template.body && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Preview
                              </label>
                              <div className="w-full p-4 border border-gray-300 rounded-md bg-gray-50 text-gray-900 min-h-[200px] overflow-y-auto">
                                <div
                                  className="prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{
                                    __html: template.body,
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Save Button */}
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleSaveTemplate(template)}
                              disabled={
                                isLoadingSave ||
                                !template.subject.trim() ||
                                !template.body.trim()
                              }
                              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              <MdSave className="mr-2" />
                              {isLoadingSave ? 'Saving...' : 'Save Template'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Read-only view
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Variables
                            </label>
                            <p className="text-sm text-gray-600">
                              {template.variables || 'No variables defined'}
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Test Recipients
                            </label>
                            <p className="text-sm text-gray-600">
                              {template.testRecipients ||
                                'No test recipients defined'}
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Body
                            </label>
                            <div className="w-full p-4 border border-gray-300 rounded-md bg-gray-50 text-gray-900 min-h-[200px] overflow-y-auto">
                              <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    template.body || 'No email body available.',
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Variable Modal */}
      <VariableModal
        isOpen={variableModal.isOpen}
        onClose={closeVariableModal}
        onSend={handleSendWithVariables}
        variables={variableModal.variables}
        templateSubject={variableModal.templateSubject}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default EmailsPage;
